'use client'
import { useEffect, useState } from 'react'
import type { L1Keys, MenuType } from './Menu'
import { NavigationModule } from './Modules/Modules'
import { hasItems, isExternal } from './navUtils'
import type { LinkCompType } from './navUtils'

export function MobileNav({
  menu,
  L1Key,
  isOpen,
  devBarDisabled,
  LinkComp,
  locale,
}: {
  menu: MenuType
  L1Key: L1Keys
  isOpen: boolean
  devBarDisabled: boolean
  LinkComp: LinkCompType
  locale?: string | undefined
}) {
  const [currentLevel, setCurrentLevel] = useState('2')

  function getActive(n: string) {
    return currentLevel == n ? true : null
  }

  function openLevel(n: string) {
    setCurrentLevel(n)
  }

  //reset menu on re-opened
  useEffect(() => {
    setCurrentLevel('2')
  }, [isOpen])

  const devBarOffset = devBarDisabled ? 0 : 24
  const menuOffset = locale == 'en-AU' ? 106 : 94
  const menuHeight = `calc(100dvh - ${menuOffset.toString()}px - ${devBarOffset.toString()}px)`

  return (
    <>
      <ul
        data-mobile-nav-open={isOpen}
        className={`lg:hidden! backdrop-blur-xs invisible absolute left-0 z-10 max-h-0 w-screen overflow-x-hidden bg-white/50 tracking-[.02em] opacity-0`}
        style={{ top: `${menuOffset.toString()}px`, height: menuHeight }}
      >
        {(() => {
          const L1 = menu
          const key = L1Key
          return (
            <li
              key={key}
              data-l1-key={key}
              data-mobile-nav
              className="absolute top-4 h-[calc(100%-32px)] w-[calc(100dvw-32px)] overflow-hidden rounded-2xl text-lg font-normal leading-tight text-white"
            >
              {/* L2 navigation */}
              <ul
                data-level="2"
                data-active={getActive('2')}
                className="absolute left-0 top-0 flex h-full w-full flex-col bg-[var(--nav-accent)] pt-2"
              >
                {L1.items?.map((L2, L2Index) => {
                  return (
                    <li key={L2.label}>
                      <div className="">
                        {hasItems(L2) ? (
                          <div
                            onClick={() => {
                              openLevel(`3.${String(L2Index)}`)
                            }}
                            data-open-level={`3.${String(L2Index)}`}
                            className="flex cursor-pointer items-center px-4"
                          >
                            <div className="py-2 leading-tight">{L2.label}</div>
                            <div className="grow"></div>
                            <div className="flex h-5 w-5 items-center justify-center rounded bg-white/25">
                              <span className="nav-chevron -rotate-90" />
                            </div>
                          </div>
                        ) : (
                          <LinkComp
                            href={L2.url ?? '#'}
                            className="nav-level-2 block px-4 py-2"
                          >
                            {L2.label}
                          </LinkComp>
                        )}
                      </div>
                      {/* L3 / L4 navigation */}
                      {hasItems(L2) && (
                        <div
                          data-level={`3.${String(L2Index)}`}
                          data-active={getActive(`3.${String(L2Index)}`)}
                          className="absolute left-full top-0 z-10 h-full w-full bg-[var(--nav-accent)] opacity-0 transition-opacity duration-500"
                        >
                          <div
                            onClick={() => {
                              openLevel(`2`)
                            }}
                            data-reset-level="2"
                            className="sticky left-0 top-0 flex cursor-pointer items-center gap-3 border-b-2 border-white bg-[var(--nav-accent)] px-4 py-4"
                          >
                            <div className="flex h-5 w-5 items-center justify-center rounded bg-white/25">
                              <span className="nav-chevron rotate-90" />
                            </div>
                            <div className="leading-tight">Back</div>
                          </div>
                          <ul className="py-4">
                            {L2.items?.map((L3, L3Index) => {
                              return (
                                <li
                                  key={`${L3.label ?? ''}_${String(L3Index)}`}
                                  className="mb-4 last:mb-0"
                                >
                                  <div className="px-4 italic text-white/50">
                                    {L3.label}
                                  </div>
                                  {hasItems(L3) && (
                                    <ul data-level="4">
                                      {L3.items?.map((L4) => {
                                        return (
                                          <li key={L4.label}>
                                            <LinkComp
                                              href={L4.url ?? '#'}
                                              className="nav-level-3 flex items-center gap-1 px-4 py-2"
                                            >
                                              {L4.label}
                                              {isExternal(L4.url) && (
                                                <span className="nav-external-link shrink-0" />
                                              )}
                                            </LinkComp>
                                          </li>
                                        )
                                      })}
                                    </ul>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                          {L2.component && (
                            <NavigationModule
                              id={L2.component.id}
                              LinkComp={LinkComp}
                            />
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </li>
          )
        })()}
      </ul>
    </>
  )
}
